import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { Checklist } from "@/components/checklist";
import { EndNote, H2, SourcesAndDisclaimer, Steps } from "@/components/guide-page";
import { PrintButton } from "@/components/print-button";
import { absUrl } from "@/lib/site";
import { howToJsonLd } from "@/lib/jsonld";

/** The habits that apply to everyone, every time. Rendered on the page and in the HowTo JSON-LD. */
const EVERY_TIME = [
  {
    name: "Use EPA-registered repellent",
    text: "Apply an EPA-registered repellent (DEET, picaridin, or oil of lemon eucalyptus) to exposed skin before going outside.",
  },
  {
    name: "Treat clothing and gear with permethrin",
    text: "Treat clothing, shoes, and gear with permethrin, or buy pre-treated items, for added protection.",
  },
  {
    name: "Avoid tall grass and brush",
    text: "Stay on cleared trails and avoid tall grass, brush, and leaf litter where ticks wait for a host.",
  },
  {
    name: "Cover up in high-risk areas",
    text: "Wear long sleeves and tuck pants into socks in brushy or wooded areas.",
  },
  {
    name: "Shower and check for ticks",
    text: "Shower within two hours of coming inside and do a thorough tick check on yourself, children, and pets.",
  },
  {
    name: "Remove attached ticks promptly",
    text: "Remove any attached tick right away with fine-tipped tweezers, pulling straight up with steady pressure.",
  },
];

export const Route = createFileRoute("/prevention")({
  head: () => ({
    meta: [
      { title: "Lone Star Tick Prevention Guide | LoneStarMap" },
      {
        name: "description",
        content:
          "Practical Lone Star tick prevention tips for families, pets, yards, hikers, campers, hunters, golfers, and travelers.",
      },
      { property: "og:title", content: "Lone Star Tick Prevention Guide" },
      {
        property: "og:description",
        content: "Prevention tips for families, pets, yards, and outdoor enthusiasts.",
      },
      { property: "og:url", content: absUrl("/prevention") },
    ],
    links: [{ rel: "canonical", href: absUrl("/prevention") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          howToJsonLd({
            name: "How to prevent Lone Star tick bites",
            description:
              "Practical steps to lower your risk of Lone Star tick bites outdoors and at home.",
            path: "/prevention",
            steps: EVERY_TIME,
          }),
        ),
      },
    ],
  }),
  component: PreventionPage,
});

const sections = [
  {
    title: "For families",
    tips: [
      "Use EPA-registered insect repellents appropriate for each age.",
      "Dress kids in long sleeves and tucked pants on wooded trails.",
      "Do tick checks after every outdoor outing, including behind ears and along the hairline.",
      "Shower within two hours of coming inside.",
    ],
  },
  {
    title: "For pets",
    tips: [
      "Talk to your veterinarian about tick prevention products.",
      "Check pets thoroughly after outdoor time: ears, neck, paws, belly.",
      "Wipe down dogs after walks in tall grass or wooded areas.",
      "Keep grooming areas clean and inspect bedding regularly.",
    ],
  },
  {
    title: "For yards",
    tips: [
      "Keep grass mowed and remove leaf litter at the lawn edge.",
      "Create a wood-chip or gravel border between lawn and wooded areas.",
      "Discourage deer and small rodents that can carry ticks.",
      "Consider tick-control products labeled for residential use.",
    ],
  },
  {
    title: "For hikers & campers",
    tips: [
      "Treat clothing and gear with permethrin before trips.",
      "Stay in the center of trails; avoid brushing tall grass.",
      "Inspect gear, tents, and packs before bringing them inside.",
      "Do a full body tick check at the end of each day.",
    ],
  },
  {
    title: "For hunters",
    tips: [
      "Wear permethrin-treated clothing in the field.",
      "Field-dress with gloves and check yourself frequently.",
      "Shower and change clothes promptly after returning.",
      "Tumble-dry hunting clothes on high heat to kill any ticks.",
    ],
  },
  {
    title: "For golfers",
    tips: [
      "Apply EPA-registered repellent on exposed skin and treat shoes/socks.",
      "Avoid searching for balls in tall rough whenever possible.",
      "Brush off your clothing and shoes before getting in the car.",
      "Do a quick tick check before heading home.",
    ],
  },
  {
    title: "For travelers",
    tips: [
      "Research tick activity for your destination.",
      "Pack repellent and permethrin-treated layers.",
      "Inspect rental gear and accommodations.",
      "Continue tick checks during and after your trip.",
    ],
  },
];

function PreventionPage() {
  return (
    <PageShell
      title="How to avoid Lone Star tick bites"
      intro="What to do before and after hiking, camping, hunting, golfing or yard work. Print it and take it with you."
    >
      <p className="print-only mb-4 text-sm">
        Lone Star Tick Prevention Guide · LoneStarMap.com · Educational only, not medical advice.
      </p>

      <div className="no-print -mt-4 sm:-mt-6">
        <PrintButton page="prevention" label="Print / Save guide" />
      </div>

      <Card>
        <div className="grid gap-x-10 gap-y-5 lg:grid-cols-[14rem_1fr]">
          <h2 className={H2}>Every time you go out</h2>
          <Steps
            items={EVERY_TIME.map((s) => ({ title: s.name, text: s.text }))}
            className="lg:border-t-0"
          />
        </div>
      </Card>

      {sections.map((s) => (
        <Card key={s.title} className="break-avoid">
          <div className="grid gap-x-10 gap-y-5 lg:grid-cols-[14rem_1fr]">
            <h2 className={H2}>{s.title}</h2>
            <Checklist items={s.tips} className="lg:border-t-0" />
          </div>
        </Card>
      ))}

      <EndNote>
        None of these steps replace medical guidance. They are practical things to do before and
        after going outdoors. If you find a tick attached, see{" "}
        <Link to="/tick-bite-tool" className="link">
          what to do after a bite
        </Link>
        . <SourcesAndDisclaimer />
      </EndNote>
    </PageShell>
  );
}
